<template>
  <component :is="layout">
    <QuickValuationTabs />
    <div class="container mx-auto px-4 pb-16">
      <div class="mt-6 mb-4">
        <h1 class="text-2xl font-light text-slate-900 tracking-tight">
          {{ $t('messages.quickValuation.tabs.history') }}
        </h1>
      </div>

      <div v-if="!evaluations.data.length" class="text-center py-16 text-slate-500">
        <div class="mb-3">{{ $t('messages.quickValuation.history.empty') }}</div>
        <Link :href="route('quick-valuation.index')" class="text-primary hover:underline">
          {{ $t('messages.quickValuation.history.emptyAction') }}
        </Link>
      </div>

      <DataTable
        v-else
        :value="evaluations.data"
        removable-sort
        striped-rows
        :paginator="evaluations.total > evaluations.per_page"
        :rows="evaluations.per_page"
        :total-records="evaluations.total"
        :first="(evaluations.current_page - 1) * evaluations.per_page"
        lazy
        data-key="id"
        data-testid="qv-history-table"
        @page="onPage"
      >
        <Column
          sortable
          field="address"
          :header="$t('messages.quickValuation.history.address')"
        >
          <template #body="{ data }">
            <Link
              :href="route('quick-valuation.result', data.id)"
              class="text-primary hover:underline"
            >
              {{ data.address || data.cadaster_id || `#${data.id}` }}
            </Link>
          </template>
        </Column>

        <Column
          sortable
          field="prediction_value"
          :header="$t('messages.quickValuation.history.value')"
          class="w-48"
        >
          <template #body="{ data }">
            <span v-if="data.prediction_value" class="tabular-nums">
              {{ formatPrice(data.prediction_value) }} €
            </span>
            <span v-else class="text-slate-400">—</span>
            <Tag
              v-if="data.prediction?.valuation_mode === 'direct'"
              data-testid="qv-history-direct-badge"
              :value="$t('messages.quickValuation.direct.badge')"
              :severity="directBadgeWarns(data.prediction) ? 'warn' : 'info'"
              class="text-xs! ml-2"
            />
          </template>
        </Column>

        <Column
          sortable
          field="created_at"
          :header="$t('messages.quickValuation.history.date')"
          class="w-40"
        />

        <Column
          :header="$t('messages.quickValuation.history.actions')"
          class="w-64"
        >
          <template #body="{ data }">
            <div class="flex gap-2">
              <Button
                v-if="canExportRow(data)"
                v-tooltip.top="!templatesForRow(data).length
                  ? $t('messages.quickValuation.shortReport.noTemplates')
                  : null"
                data-testid="qv-history-download"
                icon="pi pi-file-word"
                :label="$t('messages.quickValuation.history.download')"
                severity="secondary"
                size="small"
                :loading="hydratingId === data.id"
                :disabled="!templatesForRow(data).length || hydratingId !== null"
                @click="openDownloadDialog(data)"
              />
              <!-- RED-262 gates v1: no lühiaruanne for a price the gates decided (the
                   server refuses it too); a short reason in its place, the sentence on
                   hover, tap or keyboard focus (see showReason). -->
              <span
                v-else-if="gatesDecidesRow(data)"
                v-tooltip.top.focus="gatesNoReportReason(data.prediction.gates)"
                tabindex="0"
                data-testid="qv-history-no-report"
                class="inline-flex items-center gap-1.5 text-xs text-slate-500 cursor-help"
                @pointerenter="showReason"
                @pointerleave="hideReason"
              >
                <span class="pi pi-info-circle text-xs"></span>
                {{ data.prediction.gates.auto_value
                  ? $t('messages.quickValuation.gatesV1.noReport')
                  : $t('messages.quickValuation.gatesV1.valuerShort') }}
              </span>
              <Button
                data-testid="qv-history-delete"
                icon="pi pi-trash"
                severity="danger"
                size="small"
                text
                :aria-label="$t('messages.delete')"
                @click="confirmDelete(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- RED-212: capture host runs alongside the dialog so legacy records
         without cached maps re-render their maps on demand. Same code path as
         QuickValuationResult.vue → byte-identical maps in the resulting DOCX.
         Skipped entirely when the server already has every location map on
         disk — see `allMapsCached`. -->
    <QvMapCaptureHost
      v-if="dialogVisible && dialogEhrData?.geometry?.length && !allMapsCached"
      ref="captureHostRef"
      :evaluation-id="dialogEvaluation.id"
      :ehr-data="dialogEhrData"
      :land-board-bbox="dialogLandBoard?.bbox ?? null"
      warm-capture
    />

    <ShortReportDialog
      v-if="dialogEvaluation"
      v-model:visible="dialogVisible"
      :evaluation="dialogEvaluation"
      :templates="dialogTemplates"
      :capture-maps="captureMaps"
      :cadaster-id="dialogEvaluation.cadaster_id"
      mode="historical"
      :skip-capture="allMapsCached"
    />
  </component>
</template>

<script setup>
import axios from 'axios'
import { Link, router, usePage } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import FocusedLayout from '@/Layouts/FocusedLayout.vue'
import QuickValuationTabs from '@/components/quick-valuation/QuickValuationTabs.vue'
import QvMapCaptureHost from '@/components/quick-valuation/QvMapCaptureHost.vue'
import ShortReportDialog from '@/components/quick-valuation/ShortReportDialog.vue'
import { useFeature } from '@/composables/useFeature'
import { useQuickValuationHydration } from '@/composables/useQuickValuationHydration'
import { templatesForValuation } from '@/services/quickValuationTemplates'

const props = defineProps({
  evaluations: {
    type: Object,
    required: true,
  },
  // Every quick-valuation mall of the company, both types — filtered per row.
  quickValuationTemplates: {
    type: Array,
    default: () => [],
  },
})

const layout = computed(() =>
  usePage().props.layout === 'FocusedLayout' ? FocusedLayout : AppLayout,
)

const { t } = useI18n()
const confirm = useConfirm()
const toast = useToast()
const { hydrate, reset } = useQuickValuationHydration()

// RED-356: behind the flag a direct row gets the download too, and each row is
// offered only the malls its mode allows (same rule as the result page's
// server-filtered list). Flag off = today: kohandustabel mall, comps rows only.
// RED-262: a fallback-median row has no lühiaruanne (the server refuses it too),
// nor has a row whose price gates v1 decided (`ok` / `out_of_scope`, GatesV1::decidesPrice).
const statisticalReportEnabled = useFeature('qv-statistical-report')
const gatesDecidesRow = row => ['ok', 'out_of_scope'].includes(row.prediction?.gates?.status)
const canExportRow = row =>
  row.prediction?.valuation_mode !== 'fallback_median'
  && !gatesDecidesRow(row)
  && (row.prediction?.valuation_mode !== 'direct' || statisticalReportEnabled.value)

// With a price: why there is no report. Without one: that a price needs a valuer.
const gatesNoReportReason = gates => {
  if (gates.auto_value) return t('messages.quickValuation.gatesV1.noReportReason')
  return gates.status === 'out_of_scope'
    ? t('messages.quickValuation.gatesV1.outOfScope')
    : t('messages.quickValuation.gatesV1.valuer')
}

// The reason's tooltip opens on focus (`.focus`), so Tab and a tap open it; PrimeVue's
// hover mode never listens to focus and hides on click. A mouse focuses the span on enter
// and lets go on leave; a touch is left alone (its tap already focused the span, and the
// mouse events a browser fakes after a tap must not close it again).
const showReason = event => {
  if (event.pointerType === 'mouse') event.currentTarget.focus({ preventScroll: true })
}
const hideReason = event => {
  if (event.pointerType === 'mouse') event.currentTarget.blur()
}
const templatesForRow = row =>
  templatesForValuation(props.quickValuationTemplates, row.prediction, statisticalReportEnabled.value)

// The Otsemudel tag turns amber on a low-confidence row. RED-363 stopped
// folding the route's `confidence: low` into `low_confidence_flag` when a row
// is stored; with the flag off the tag still reads it, as LIVE does (Toomas
// 02.10 15:40: LIVE unchanged until he approves). RED-262 gates v1: a direct
// row whose price the gates decided never warns — its grade speaks for it.
const directBadgeWarns = prediction =>
  !gatesDecidesRow({ prediction })
  && (Boolean(prediction?.low_confidence_flag)
    || (!statisticalReportEnabled.value && prediction?.confidence === 'low'))

const dialogVisible = ref(false)
const dialogEvaluation = ref(null)
const dialogTemplates = ref([])
const dialogEhrData = ref(null)
const dialogLandBoard = ref(null)
const dialogMapsCached = ref(null)
const hydratingId = ref(null)
const captureHostRef = ref(null)

// RED-212: full coverage means we can skip the OpenLayers re-capture and the
// implied Maa-amet WMS tile fetches — server falls back to its on-disk PNGs.
// ESG maps are best-effort (server tolerates gaps) so we key the decision on
// the four canonical location maps only.
const REQUIRED_LOCATION_MAPS = ['mikro', 'makro', 'kataster', 'infra']
const allMapsCached = computed(() => {
  const location = dialogMapsCached.value?.location
  if (!Array.isArray(location)) return false
  return REQUIRED_LOCATION_MAPS.every(key => location.includes(key))
})

// RED-212: bridge the export dialog's `captureMaps` callback through to the
// mounted QvMapCaptureHost. Identical signature/contract to QuickValuationResult.vue.
const captureMaps = (onProgress) =>
  captureHostRef.value?.captureAll
    ? captureHostRef.value.captureAll(onProgress)
    : Promise.resolve({})

// RED-212: hydrate the Pinia stores from the persisted prediction *before*
// opening the export dialog, so the resulting kohandustabel matches the live
// QuickValuationResult.vue export. Without this, ShortReportDialog's
// `collectTransactions()` returns null and the server falls back to the
// stripped 3-row template.
const openDownloadDialog = async row => {
  if (hydratingId.value !== null) return
  hydratingId.value = row.id

  try {
    const { data } = await axios.get(
      route('quick-valuation.export-context', row.id),
    )
    await hydrate({
      evaluation: data.evaluation,
      ehrData: data.ehrData,
      landBoard: data.landBoard,
      prediction: data.prediction,
      buildingPartId: data.building_part_id,
    })
    dialogEvaluation.value = {
      ...row,
      ...data.evaluation,
      prediction: data.prediction,
    }
    // The same list the row's button counted, so an enabled button never
    // opens an empty dropdown.
    dialogTemplates.value = templatesForRow(row)
    dialogEhrData.value = data.ehrData ?? null
    dialogLandBoard.value = data.landBoard ?? null
    dialogMapsCached.value = data.maps_cached ?? null
    dialogVisible.value = true
  } catch (err) {
    console.warn('[QuickValuationHistory] hydration failed', err)
    toast.add({
      severity: 'error',
      summary: t('messages.error'),
      detail: t('messages.quickValuation.shortReport.error'),
      life: 5000,
    })
  } finally {
    hydratingId.value = null
  }
}

watch(dialogVisible, visible => {
  if (!visible) {
    // Clear selection + reset stores after the dialog's close animation so
    // bindings stay stable and the next row's hydration starts clean.
    setTimeout(() => {
      dialogEvaluation.value = null
      dialogTemplates.value = []
      dialogEhrData.value = null
      dialogLandBoard.value = null
      dialogMapsCached.value = null
      reset()
    }, 200)
  }
})

const confirmDelete = row => {
  const label = row.address || row.cadaster_id || `#${row.id}`
  confirm.require({
    header: t('messages.quickValuation.history.deleteConfirmTitle'),
    message: t('messages.quickValuation.history.deleteConfirmMessage', { address: label }),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('messages.delete'),
    rejectLabel: t('messages.cancel'),
    acceptClass: 'p-button-danger',
    accept: () => {
      router.delete(route('quick-valuation.destroy', row.id), {
        preserveScroll: true,
        onSuccess: () => {
          toast.add({
            severity: 'success',
            detail: t('messages.quickValuation.history.deleted'),
            life: 3000,
          })
        },
        onError: () => {
          toast.add({
            severity: 'error',
            summary: t('messages.error'),
            detail: t('messages.quickValuation.history.deleteError'),
            life: 5000,
          })
        },
      })
    },
  })
}

const onPage = event => {
  router.get(
    route('quick-valuation.history'),
    { page: event.page + 1 },
    { preserveScroll: true, preserveState: true, only: ['evaluations'] },
  )
}

const formatPrice = value => {
  if (!value) return '-'
  return new Intl.NumberFormat('et-EE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}
</script>
